(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  // Fix for Android BFCache (back-forward cache) blank page bug.
  //
  // When Android Chrome restores a page from BFCache, elements with a
  // `view-transition-name` keep a stale GPU compositor layer that never gets
  // repainted, leaving the page invisible until the user switches apps.
  //
  // The fix: on a BFCache restore (pageshow with persisted:true), strip all
  // view-transition-name values, force a synchronous reflow to flush the stale
  // GPU layers, then restore the names so the compositor creates fresh ones.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    var namedEls = [];

    // 1. Inline-style names.
    document.querySelectorAll('[style*="view-transition-name"]').forEach(function (el) {
      var name = el.style.viewTransitionName;
      if (name) {
        namedEls.push({ el: el, name: name, inline: true });
        el.style.viewTransitionName = 'none';
      }
    });

    // 2. The sticky header gets its name from the stylesheet, not inline style.
    //    Force-override it to break the stale compositor layer.
    var stickyHeader = document.querySelector('[data-sticky-state="active"]');
    if (stickyHeader) {
      namedEls.push({ el: stickyHeader, name: '', inline: false });
      stickyHeader.style.viewTransitionName = 'none';
    }

    if (namedEls.length === 0) {
      // Nothing named — still force a full repaint as a safety net.
      document.body.style.display = 'none';
      // eslint-disable-next-line no-unused-expressions
      document.body.offsetHeight;
      document.body.style.display = '';
      return;
    }

    // Force the browser to process the removal synchronously (flushes stale GPU layers).
    // eslint-disable-next-line no-unused-expressions
    document.body.offsetHeight;

    // Restore names so the compositor builds fresh layers.
    namedEls.forEach(function (item) {
      if (item.inline) {
        item.el.style.viewTransitionName = item.name;
      } else {
        item.el.style.removeProperty('view-transition-name');
      }
    });
  });

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
